import { isNil } from 'ramda';
import { Config } from '~admin/config';
import { EventData } from '~admin/model/event-data';
import { EventRestrictions, EventTimelineGroup } from '~admin/model/event-translation-data';
import { MediaData } from '~admin/model/media-data';
import { mapMediaData } from '~admin/model/media-data.mapper';
import { EventDatetimeDetailsFormData } from '~admin/view/components/details/event-details/event-datetime-details.component';
import { EventLocationFormData } from '~admin/view/components/details/event-details/event-location-details';
import { EventMediaFormData } from '~admin/view/components/details/event-details/event-media-details.component';
import { EventOptionsFormData } from '~admin/view/components/details/event-details/event-options-details.component';
import { EventRestrictionsFormData } from '~admin/view/components/details/event-details/event-restrictions/event-restrictions.component';
import { EventTimelineFormData } from '~admin/view/components/details/event-details/event-timeline-details/event-timeline-details.component';
import { EventFormData } from '~admin/view/components/details/event-form-data';
import { EventSponsorDetailsFromData } from '~admin/view/components/details/sponsor-details/sponsor-details.component';
import { TranslationFormData } from '~admin/view/components/ui/form/fields/field-translation.component';
import { MediaType } from '~client/model/media-type';
import { datetime } from '~common/helper/datetime';
import { Language } from '~common/i18n';
import { EventMerchandiseRestrictionsFormData } from '~admin/view/components/details/event-details/event-restrictions/event-merchandise-restrictions.component';
import { EventRestrictionType } from '~client/model/event-restriction-type';
import { isNilOrEmpty } from '~common/helper/global';

/**
 * Returns initial values for a translation field
 * @param date
 */
export const getFormattedDate = (date: string): string =>
  datetime.parse(date)?.format(Config.dateTimeFormats.dateTimeField);

/**
 * Returns initial values for a translation field
 * @param language
 * @param value
 */
const getTranslation = (language: Language, value: string): TranslationFormData<string> => ({
  language,
  value,
});

/**
 * Pushes translation to the translation array if it exists
 * @param array
 * @param language
 * @param value
 */
const pushTranslation = (
  array: TranslationFormData<string>[],
  language: Language,
  value: string,
): void => {
  if (!isNil(value)) {
    array.push(getTranslation(language, value));
  }
};

/**
 * Returns event name/description and its translations
 * @param event
 */
const getEventBaseDetailsInitialValues = (
  event: EventData,
): Pick<EventFormData, 'eventName' | 'eventDescription' | 'eventLanguage'> => {
  const eventName: TranslationFormData<string>[] = [
    getTranslation(Language.ENGLISH, event.eventName),
  ];

  const eventDescription: TranslationFormData<string>[] = [
    getTranslation(Language.ENGLISH, event.eventDescription),
  ];

  event.eventTranslations.eventTranslations.map((translation) => {
    pushTranslation(eventName, translation.language, translation.eventName);
    pushTranslation(eventDescription, translation.language, translation.eventDescription);
  });
  return { eventName, eventDescription, eventLanguage: event.language };
};

/**
 * Returns landscape video media
 */
const getLandscapeVideoMedia = (eventMedia: MediaData[]): MediaData =>
  eventMedia.find(
    (media) =>
      media.type === MediaType.VIDEO &&
      (isNil(media.width) || isNil(media.height) || media.width > media.height),
  );

/**
 * Returns portrait video media
 */
const getPortraitVideoMedia = (eventMedia: MediaData[]): MediaData =>
  eventMedia.find((media) => media.type === MediaType.SEATMAP && media.height > media.width);

/**
 * Returns initial values for event media details
 */
const getEventMediaInitialValues = (event: EventData): EventMediaFormData => {
  const { avatarMedia, defaultMedia, emailMedia, eventMedia } = event;

  return {
    avatarMedia,
    defaultMedia,
    emailMedia,
    landscapeVideo: getLandscapeVideoMedia(eventMedia),
    portraitVideo: getPortraitVideoMedia(eventMedia),
  };
};

/**
 * Returns initial values for location details
 */
const getEventLocationInitialValues = (event: EventData): EventLocationFormData => {
  const { eventLocationId, seatConfigurationId, eventAccessibilityType, seatMap } = event;
  return {
    eventLocationId,
    seatConfigurationId,
    eventAccessibilityType,
    seatMapMedia: seatMap,
  };
};

/**
 * Returns initial values for event datetime details
 */
const getEventDatetimeInitialValues = (event: EventData): EventDatetimeDetailsFormData => {
  const { eventStartDate, eventEndDate, discoverStartDate, discoverEndDate, doorsOpenDate } = event;
  return {
    eventDateTimeStart: getFormattedDate(eventStartDate),
    eventDateTimeEnd: getFormattedDate(eventEndDate),
    discoverDateStart: getFormattedDate(discoverStartDate),
    discoverDateEnd: getFormattedDate(discoverEndDate),
    doorsOpenDate: getFormattedDate(doorsOpenDate),
  };
};

/**
 * Returns initial values for event additional options details
 */
const getEventOptionsInitialValues = (event: EventData): EventOptionsFormData => {
  const {
    ticketLimit,
    currency,
    vanityName,
    liveStreamReplay,
    isPrivateEvent,
    facebookPixelId,
    externalNotificationSubscriptions,
    eventTranslations,
  } = event;
  const notificationSubscriptions: TranslationFormData<string>[] = [];
  if (!isNilOrEmpty(externalNotificationSubscriptions)) {
    notificationSubscriptions.push(
      getTranslation(Language.ENGLISH, externalNotificationSubscriptions[0].displayString),
    );
    const subscriptionTranslations = eventTranslations.eventNotificationSubscriptions.find(
      (translation) => translation.subscriptionId === externalNotificationSubscriptions[0].id,
    );
    subscriptionTranslations?.subscriptionTranslations?.forEach((translation) => {
      pushTranslation(notificationSubscriptions, translation.language, translation.displayString);
    });
  }

  return {
    ticketLimit,
    currency,
    vanityName,
    isPrivateEvent,
    liveStreamReplay,
    facebookPixelId,
    externalNotificationSubscriptionsId: isNilOrEmpty(externalNotificationSubscriptions)
      ? null
      : externalNotificationSubscriptions[0].id,
    externalNotificationSubscriptionsText: notificationSubscriptions,
  };
};

/**
 * Returns initial values for event timeline details
 */
const getEventTimelineInitialValues = (event: EventData): EventTimelineFormData => {
  return {
    eventTimelineGroups: event.eventTimelineGroups?.map((group) => {
      const { eventTimelineGroups } = event.eventTranslations;
      const timelineGroupTranslations: EventTimelineGroup = eventTimelineGroups.find(
        (translation) => group.id === translation.groupId,
      );

      const groupTitle: TranslationFormData<string>[] = [
        getTranslation(Language.ENGLISH, group.title),
      ];

      timelineGroupTranslations?.groupTranslations?.map((translation) => {
        pushTranslation(groupTitle, translation.language, translation.title);
      });

      return {
        title: groupTitle,
        eventTimelineItems: group.timeline?.map((item) => {
          const groupItemTranslations = timelineGroupTranslations?.groupItems?.find(
            (translation) => item.rank === translation.rank,
          );

          const itemTitle: TranslationFormData<string>[] = [
            getTranslation(Language.ENGLISH, item.title),
          ];
          const itemDescription: TranslationFormData<string>[] = [
            getTranslation(Language.ENGLISH, item.description),
          ];

          groupItemTranslations?.itemTranslations.map((translation) => {
            pushTranslation(itemTitle, translation.language, translation.title);
            pushTranslation(itemDescription, translation.language, translation.description);
          });

          return {
            title: itemTitle,
            description: itemDescription,
            rank: item.rank,
          };
        }),
      };
    }),
  };
};

/**
 * Returns initial values for event restriction details and its translations
 */
const getEventRestrictionInitialValues = (event: EventData): EventRestrictionsFormData => {
  // Filter out merch restrictions which are handled as a separate section that only gets
  // displayed in emails.
  return {
    eventRestrictions: event.eventRestrictions
      ?.filter(
        ({ eventRestrictionType }) => eventRestrictionType !== EventRestrictionType.MERCHANDISE,
      )
      .map(({ description, url, id }) => {
        const { eventRestrictions } = event.eventTranslations;
        const restrictionTranslations: EventRestrictions = eventRestrictions.find(
          (translation) => id === translation.restrictionId,
        );

        const descriptionTranslations: TranslationFormData<string>[] = [
          getTranslation(Language.ENGLISH, description),
        ];
        const urlTranslations: TranslationFormData<string>[] = [
          getTranslation(Language.ENGLISH, url),
        ];

        restrictionTranslations?.restrictionTranslations.map((translation) => {
          pushTranslation(descriptionTranslations, translation.language, translation.description);
          pushTranslation(urlTranslations, translation.language, translation.url);
        });

        return {
          description: descriptionTranslations,
          url: urlTranslations,
        };
      }),
  };
};

const getEventMerchandiseRestrictionInitialValues = (
  event: EventData,
): EventMerchandiseRestrictionsFormData => {
  const merchRestriction = event.eventRestrictions.find(
    (restriction) => restriction.eventRestrictionType === EventRestrictionType.MERCHANDISE,
  );
  const description: TranslationFormData<string>[] = [
    getTranslation(Language.ENGLISH, merchRestriction?.description),
  ];
  const secondaryDescription: TranslationFormData<string>[] = [
    getTranslation(Language.ENGLISH, merchRestriction?.secondaryDescription),
  ];
  const url: TranslationFormData<string>[] = [
    getTranslation(Language.ENGLISH, merchRestriction?.url),
  ];

  const restrictionTranslations: EventRestrictions = event.eventTranslations?.eventRestrictions?.find(
    (translation) => merchRestriction?.id === translation.restrictionId,
  );
  restrictionTranslations?.restrictionTranslations.map((translation) => {
    pushTranslation(description, translation.language, translation.description);
    pushTranslation(secondaryDescription, translation.language, translation.secondaryDescription);
    pushTranslation(url, translation.language, translation.url);
  });

  return { merchText: secondaryDescription, merchButtonText: description, merchButtonUrl: url };
};

/**
 * Returns initial values for event sponsor details
 */
const getEventSponsorsInitialValues = (event: EventData): EventSponsorDetailsFromData => {
  return {
    sponsorGroups: event.sponsorGroups?.map((group) => ({
      height: group.height,
      sponsors: group.sponsorList?.map(({ icon, id, url }) => ({
        icon: mapMediaData(icon),
        id,
        url,
      })),
    })),
  };
};

/**
 * Returns initial values for create event form
 */
export const getEditEventInitialValues = (event: EventData): EventFormData => ({
  clientId: event.clientId,
  ...getEventBaseDetailsInitialValues(event),
  ...getEventLocationInitialValues(event),
  ...getEventMediaInitialValues(event),
  ...getEventDatetimeInitialValues(event),
  ...getEventOptionsInitialValues(event),
  ...getEventTimelineInitialValues(event),
  ...getEventRestrictionInitialValues(event),
  ...getEventMerchandiseRestrictionInitialValues(event),
  ...getEventSponsorsInitialValues(event),
});



// WEBPACK FOOTER //
// ./view/components/edit-event/helper/edit-event-initial-values.helper.ts